/**
 * Gift Selection Hook for Cart-Level Gifts
 * Uses unified store with entityType = 'cart' and entityId = cartId
 * Implements Public API pattern for store access
 */
import { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from '@legendapp/state/react';
import { type SelectedGift } from '../types';
import { getGiftSelectionStore$, createNewGiftSelectionSession$, type GiftSelectionSession } from '../stores';
import { useCart } from '@kidslabs/module-quote';
import type { Observable } from '@legendapp/state';
import { useTranslation } from 'next-i18next';

export interface UseCartGiftSelectionProps {
    createNewSession?: boolean;
}

export interface UseCartGiftSelectionReturn {
    selectedGifts: SelectedGift[];
    selectGift: (gift: SelectedGift) => void;
    removeGift: (giftId: string) => void;
    updateQuantity: (giftId: string, quantity: number) => void;
    isComplete: boolean;
    store$: Observable<GiftSelectionSession>;
    ruleName: string;
    removeAllSelectedGift: () => void;
    hasSelectedCartGift: () => boolean;
}

/**
 * Hook for managing cart-level gift selection
 * Uses unified store with entityType='cart' and entityId=cartId
 */
export const useCartGiftSelection = ({
    createNewSession = false,
}: UseCartGiftSelectionProps): UseCartGiftSelectionReturn => {
    const { t } = useTranslation('flash-sale');
    // Get cartId from cart store
    const { cartId } = useCart();

    // Initialize cart gift store with entityType = 'cart'
    useEffect(() => {
        if (createNewSession && cartId) {
            createNewGiftSelectionSession$(cartId, 'cart');
        }
    }, [createNewSession, cartId]);

    // Get cart gift selection store (entityType = 'cart', entityId = cartId)
    const store$ = useMemo(() => {
        return getGiftSelectionStore$(cartId, 'cart');
    }, [cartId]);

    // Subscribe to selected gifts reactively
    const selectedGifts = useSelector(() => store$.selectedGifts.get() ?? []);

    // Selected rule name
    const ruleName = useMemo(() => {
        return selectedGifts?.at(0)?.giftData?.rule?.name ?? t('text_selected_gifts', 'Quà tặng đã chọn');
    }, [selectedGifts, t]);

    /**
     * Select a gift for cart — reads from and writes to store$ directly
     * to avoid sessionId mismatch issues between store$ and public API.
     *
     * Cart-level policy: only one gift across all rules (picking a new gift replaces any previous).
     */
    const selectGift = useCallback(
        (gift: SelectedGift) => {
            store$.selectedGifts.set([gift]);
        },
        [store$]
    );

    // Remove a gift from cart selection
    const removeGift = useCallback(
        (giftId: string) => {
            const currentGifts = store$.selectedGifts.peek() ?? [];
            const updatedGifts = currentGifts.filter((g: SelectedGift) => g.id !== giftId);
            store$.selectedGifts.set(updatedGifts);
        },
        [store$]
    );

    // Update quantity of selected gift
    const updateQuantity = useCallback(
        (giftId: string, quantity: number) => {
            const currentGifts = store$.selectedGifts.peek() ?? [];
            const updatedGifts = currentGifts.map((g: SelectedGift) =>
                g.id === giftId ? { ...g, quantity, giftSlots: quantity } : g
            );
            store$.selectedGifts.set(updatedGifts);
        },
        [store$]
    );

    const removeAllSelectedGift = useCallback(() => {
        store$.selectedGifts.set([]);
    }, [store$]);

    const hasSelectedCartGift = useCallback(() => {
        return store$.selectedGifts.get()?.length > 0;
    }, [store$]);

    // Check if selection is complete (at least one gift selected)
    const isComplete = useMemo(() => {
        return selectedGifts.length > 0;
    }, [selectedGifts]);

    return {
        ruleName,
        selectedGifts,
        selectGift,
        removeGift,
        updateQuantity,
        isComplete,
        store$,
        removeAllSelectedGift,
        hasSelectedCartGift,
    };
};
